import type { StatsGranularity } from '../services/types';

/**
 * Formatierungs-Utilities fuer das Statistik-Dashboard:
 * deutsche Zahlenformatierung und Perioden-Labels je Granularitaet.
 */

/** Deutscher Zahlenformatierer (Tausenderpunkt, z. B. "1.234"). */
const GERMAN_NUMBER_FORMAT = new Intl.NumberFormat('de-DE');

/** Formatiert eine Zahl im deutschen Zahlenformat (z. B. 1234 -> "1.234"). */
export function formatGermanNumber(value: number): string {
  return GERMAN_NUMBER_FORMAT.format(value);
}

/** Kurze deutsche Monatsnamen fuer Achsen-Ticks ("Aug 26"). */
const SHORT_MONTHS: readonly string[] = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
];

/** Volle deutsche Monatsnamen fuer Tooltips und Tabellen ("August 2026"). */
const LONG_MONTHS: readonly string[] = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

/** Zerlegtes ISO-Datum (nur Datumsanteil). */
interface IsoDateParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/**
 * Vollstaendiges ISO-Datum (endverankert!). Ein nicht verankertes Muster
 * wuerde Muell wie "2026-13-99xyz" akzeptieren und still zu einem falschen
 * Datum normalisieren.
 */
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Tage je Monat im Nicht-Schaltjahr (Index 0 = Januar). */
const DAYS_PER_MONTH: readonly number[] = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Gregorianische Schaltjahr-Regel. */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** Tatsaechliche Monatslaenge inklusive Schaltjahr (month: 1..12). */
function daysInMonth(year: number, month: number): number {
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }
  return DAYS_PER_MONTH[month - 1];
}

/**
 * Parst ein vollstaendiges ISO-Datum ("2026-08-04").
 *
 * Streng validiert: exakt YYYY-MM-DD, Monat 1..12 und Tag gegen die
 * tatsaechliche Monatslaenge (Schaltjahr beachtet). Liefert null bei
 * jedem anderen Wert; die Formatter fallen dann auf den Rohwert zurueck,
 * statt `undefined`-Monatsnamen oder still verschobene Datumswerte zu
 * erzeugen.
 */
function parseIsoDate(value: string): IsoDateParts | null {
  const match = ISO_DATE_PATTERN.exec(value);
  if (match === null) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) {
    return null;
  }
  if (day < 1 || day > daysInMonth(year, month)) {
    return null;
  }
  return { year, month, day };
}

/** ISO-Kalenderwoche samt zugehoerigem ISO-Jahr. */
interface IsoWeek {
  readonly week: number;
  readonly year: number;
}

/**
 * Berechnet die ISO-8601-Kalenderwoche (Montag als Wochenbeginn) fuer ein
 * Datum. UTC-basiert, damit keine Zeitzonen-Verschiebung hineinspielt.
 */
function isoWeekOf(parts: IsoDateParts): IsoWeek {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  // Auf den Donnerstag derselben ISO-Woche verschieben
  const dayNumber = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNumber + 3);
  const isoYear = date.getUTCFullYear();
  // Erster Donnerstag des ISO-Jahres
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstDayNumber = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNumber + 3);
  const week =
    1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000));
  return { week, year: isoYear };
}

/** Zweistellige Formatierung (4 -> "04"). */
function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Ermittelt das ISO-Wochenjahr eines Perioden-Datums (oder null bei
 * ungueltigem Datum). Wird gebraucht, um zu entscheiden, ob Wochen-Ticks
 * das Jahr mitfuehren muessen (siehe formatPeriodShort).
 */
export function isoWeekYearOf(periodStart: string): number | null {
  const parts = parseIsoDate(periodStart);
  return parts === null ? null : isoWeekOf(parts).year;
}

/** Optionen der Kurz-Formatierung. */
export interface PeriodShortOptions {
  /**
   * Haengt bei Wochen das zweistellige ISO-Jahr an ("KW 32/26").
   * Noetig, sobald der dargestellte Zeitraum mehr als ein ISO-Jahr
   * umfasst, weil "KW 32" sonst mehrfach auf der Achse steht.
   */
  readonly includeWeekYear?: boolean;
}

/**
 * Kurzes Perioden-Label fuer Achsen-Ticks:
 * Tag "04.08.", Woche "KW 32" (bzw. "KW 32/26"), Monat "Aug 26",
 * Jahr "2026".
 */
export function formatPeriodShort(
  periodStart: string,
  granularity: StatsGranularity,
  options?: PeriodShortOptions
): string {
  const parts = parseIsoDate(periodStart);
  if (parts === null) {
    return periodStart;
  }
  switch (granularity) {
    case 'day':
      return `${pad2(parts.day)}.${pad2(parts.month)}.`;
    case 'week': {
      const isoWeek = isoWeekOf(parts);
      return options?.includeWeekYear === true
        ? `KW ${isoWeek.week}/${pad2(isoWeek.year % 100)}`
        : `KW ${isoWeek.week}`;
    }
    case 'month':
      return `${SHORT_MONTHS[parts.month - 1]} ${pad2(parts.year % 100)}`;
    case 'year':
      return String(parts.year);
  }
}

/**
 * Ausfuehrliches Perioden-Label fuer Tooltips und Tabellenzeilen:
 * Tag "04.08.2026", Woche "KW 32 2026", Monat "August 2026", Jahr "2026".
 */
export function formatPeriodLong(periodStart: string, granularity: StatsGranularity): string {
  const parts = parseIsoDate(periodStart);
  if (parts === null) {
    return periodStart;
  }
  switch (granularity) {
    case 'day':
      return `${pad2(parts.day)}.${pad2(parts.month)}.${parts.year}`;
    case 'week': {
      const isoWeek = isoWeekOf(parts);
      return `KW ${isoWeek.week} ${isoWeek.year}`;
    }
    case 'month':
      return `${LONG_MONTHS[parts.month - 1]} ${parts.year}`;
    case 'year':
      return String(parts.year);
  }
}
